/**
 * Append-only point buffers for the LIVE timeseries page.
 *
 * The dataset/day pages rebuild every series from scratch on each load, which is right for a one-shot
 * fetch but wrong for a stream: a live tick would then cost O(window) instead of O(new points). At the
 * 30-minute ceiling that is ~36k samples per boat per series, so a full rebuild several times a second
 * is exactly the shape of stall that {@link ../constants/liveRetention} exists to prevent.
 *
 * So this module keeps one ascending array per (chart, series, boat) plus the last timestamp appended,
 * and each tick only appends what is genuinely new and drops what has aged out of the window.
 *
 * Display values (`displayY`, what the renderer actually plots via `plotYFromSeriesPoint`) split by
 * line type, and that split is what makes incremental appends safe:
 *
 *  - `standard` — `precomputeSeriesDisplayTimeline` is a pure per-point map (`displayY = y`), so it is
 *    computed at append time and never revisited. This is the common case.
 *  - everything else (cumulative / derivative / difference / …) — order-dependent across the whole
 *    series, with carry across gap-split segments, so it CANNOT be appended incrementally and is
 *    recomputed over the (bounded) window on each materialize.
 */

import { getRowChannelValue } from "./channelRef";
import { precomputeSeriesDisplayTimelineAsync } from "./timeseriesDisplayTimelinePrecompute";
import { normalizeLineType } from "./timeseriesSeriesTransforms";

/** A point in the shape `buildChartSeriesData` produces, so the renderer/legend read it unchanged. */
export interface LiveSeriesPoint {
  x: Date;
  y: number;
  displayY?: number | null;
  Datetime?: unknown;
  [key: string]: unknown;
}

/** How to pull one series' value out of a live row. */
export interface LiveSeriesPointSpec {
  /** x-axis channel name as stored on the series — `"Datetime"` for every live chart. */
  xName: string;
  /** y key as stored on the series, i.e. `"Bsp_kts"` or fleet's `"Bsp_kts - ALINGHI"`. */
  yName: string;
  /** Channel actually read from the row (the fleet ` - BOAT` suffix stripped off). */
  yChannel: string;
}

/**
 * Stale points are dropped in batches rather than one at a time: each prune is an O(n) array copy, so
 * trimming a single expired sample every tick would be pure overhead. Retention is a soft bound anyway
 * ({@link ../constants/liveRetention} already keeps slack beyond the visible window).
 */
export const LIVE_BUFFER_PRUNE_BATCH = 256;

/** Epoch ms for a live row, which carries `timestamp` (ms) and usually a `Datetime`. */
export function liveRowTimestampMs(row: Record<string, unknown>): number {
  const ts = row.timestamp;
  if (typeof ts === "number" && Number.isFinite(ts)) {
    // Live rows are ms, but tolerate seconds so a replayed/parquet-shaped row is not read as 1970.
    return ts > 0 && ts < 1e11 ? ts * 1000 : ts;
  }
  const dt = row.Datetime ?? row.datetime;
  if (dt instanceof Date) {
    const t = dt.getTime();
    return Number.isFinite(t) ? t : Number.NaN;
  }
  if (dt !== undefined && dt !== null) {
    const t = new Date(dt as string | number).getTime();
    return Number.isFinite(t) ? t : Number.NaN;
  }
  return Number.NaN;
}

/**
 * Map one live row to a series point, or `null` when the row carries no usable value for this series.
 *
 * `displayY` is left unset here — {@link materialize} fills it, because for derived line types it is not
 * a function of this point alone.
 */
export function liveRowToSeriesPoint(
  row: Record<string, unknown>,
  spec: LiveSeriesPointSpec,
): LiveSeriesPoint | null {
  const tsMs = liveRowTimestampMs(row);
  if (!Number.isFinite(tsMs)) {return null;}

  const rawY = getRowChannelValue(row, spec.yChannel);
  if (rawY === undefined || rawY === null) {return null;}
  const y = typeof rawY === "number" ? rawY : Number(rawY);
  if (!Number.isFinite(y)) {return null;}

  const x = new Date(tsMs);
  const point: LiveSeriesPoint = {
    x,
    y,
    Datetime: row.Datetime ?? x,
    [spec.xName]: spec.xName === "Datetime" ? (row.Datetime ?? x) : getRowChannelValue(row, spec.xName),
    [spec.yName]: y,
  };

  // Carried through so filters/tooltips behave as they do on the dataset/day pages.
  const race = getRowChannelValue(row, "Race_number");
  if (race !== undefined) {point.Race_number = race;}
  const leg = getRowChannelValue(row, "Leg_number");
  if (leg !== undefined) {point.Leg_number = leg;}
  const grade = getRowChannelValue(row, "Grade");
  if (grade !== undefined) {point.Grade = grade;}

  return point;
}

/** Buffer key for one drawn line: one channel of one chart for one boat. */
export function liveSeriesKey(chartIndex: number, seriesIndex: number, sourceId: number): string {
  return `${chartIndex}:${seriesIndex}:${sourceId}`;
}

interface BufferEntry {
  points: LiveSeriesPoint[];
  lastTs: number;
}

export interface LiveTimeseriesBuffer {
  /**
   * Append rows for one line. Rows may arrive in any order and may repeat; only those strictly newer
   * than the last appended timestamp are kept, so duplicate WS batches and an overlapping backfill are
   * both idempotent. Returns how many points were actually added.
   */
  appendRows(key: string, rows: Record<string, unknown>[], spec: LiveSeriesPointSpec): number;
  /** Seed a line from a backfill, replacing whatever it held. Rows need not be sorted. */
  seedRows(key: string, rows: Record<string, unknown>[], spec: LiveSeriesPointSpec): number;
  /** Drop points older than `minTs` across every line (batched — see LIVE_BUFFER_PRUNE_BATCH). */
  prune(minTs: number): void;
  /** Raw ascending points for one line (the live array — do not mutate). */
  points(key: string): LiveSeriesPoint[];
  /** Display-ready points for one line, with `displayY` resolved for this line type. */
  materialize(key: string, lineType: unknown, segmentGapMs: number): Promise<LiveSeriesPoint[]>;
  /** Forget lines that are no longer drawn (chart switched, boat deselected). */
  retainOnly(keys: Iterable<string>): void;
  /** Newest timestamp held across every line, or null when empty. */
  latestTs(): number | null;
  /** Total points held, for diagnostics. */
  size(): number;
  clear(): void;
}

/** First index whose point is at or after `minTs` (binary search over the ascending array). */
function lowerBoundByTime(points: LiveSeriesPoint[], minTs: number): number {
  let lo = 0;
  let hi = points.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (points[mid].x.getTime() >= minTs) {hi = mid;}
    else {lo = mid + 1;}
  }
  return lo;
}

export function createLiveTimeseriesBuffer(): LiveTimeseriesBuffer {
  const entries = new Map<string, BufferEntry>();

  const ensure = (key: string): BufferEntry => {
    let entry = entries.get(key);
    if (!entry) {
      entry = { points: [], lastTs: Number.NEGATIVE_INFINITY };
      entries.set(key, entry);
    }
    return entry;
  };

  const appendPointsAscending = (entry: BufferEntry, points: LiveSeriesPoint[]): number => {
    let added = 0;
    for (const p of points) {
      const t = p.x.getTime();
      if (!(t > entry.lastTs)) {continue;}
      entry.points.push(p);
      entry.lastTs = t;
      added++;
    }
    return added;
  };

  const toSortedPoints = (
    rows: Record<string, unknown>[],
    spec: LiveSeriesPointSpec,
  ): LiveSeriesPoint[] => {
    const points: LiveSeriesPoint[] = [];
    for (const row of rows) {
      const p = liveRowToSeriesPoint(row, spec);
      if (p) {points.push(p);}
    }
    points.sort((a, b) => a.x.getTime() - b.x.getTime());
    return points;
  };

  return {
    appendRows(key, rows, spec) {
      if (!rows || rows.length === 0) {return 0;}
      return appendPointsAscending(ensure(key), toSortedPoints(rows, spec));
    },

    seedRows(key, rows, spec) {
      const entry = ensure(key);
      entry.points = [];
      entry.lastTs = Number.NEGATIVE_INFINITY;
      if (!rows || rows.length === 0) {return 0;}
      return appendPointsAscending(entry, toSortedPoints(rows, spec));
    },

    prune(minTs) {
      if (!Number.isFinite(minTs)) {return;}
      for (const entry of entries.values()) {
        if (entry.points.length === 0) {continue;}
        const cut = lowerBoundByTime(entry.points, minTs);
        if (cut <= 0) {continue;}
        // Only pay the array copy once enough points have aged out; retention is a soft bound and
        // liveRetention already keeps slack beyond the visible window.
        if (cut < LIVE_BUFFER_PRUNE_BATCH && cut < entry.points.length) {continue;}
        entry.points = entry.points.slice(cut);
      }
    },

    points(key) {
      return entries.get(key)?.points ?? [];
    },

    async materialize(key, lineType, segmentGapMs) {
      const entry = entries.get(key);
      if (!entry || entry.points.length === 0) {return [];}

      if (normalizeLineType(lineType) === "standard") {
        // Pure per-point: fill displayY in place once and reuse it on every later tick.
        for (let i = entry.points.length - 1; i >= 0; i--) {
          const p = entry.points[i];
          if (p.displayY !== undefined) {break;}
          p.displayY = Number.isFinite(p.y) ? p.y : null;
        }
        return entry.points;
      }

      // Derived line types depend on the whole series (carry across gap-split segments), so recompute
      // over the bounded window. This is why the live window has a hard ceiling.
      return precomputeSeriesDisplayTimelineAsync(entry.points, lineType, segmentGapMs);
    },

    retainOnly(keys) {
      const keep = keys instanceof Set ? keys : new Set(keys);
      for (const key of [...entries.keys()]) {
        if (!keep.has(key)) {entries.delete(key);}
      }
    },

    latestTs() {
      let latest = Number.NEGATIVE_INFINITY;
      for (const entry of entries.values()) {
        if (entry.lastTs > latest) {latest = entry.lastTs;}
      }
      return Number.isFinite(latest) ? latest : null;
    },

    size() {
      let total = 0;
      for (const entry of entries.values()) {total += entry.points.length;}
      return total;
    },

    clear() {
      entries.clear();
    },
  };
}
